/* eslint-disable no-nested-ternary */
import { Modal, SkeletonText } from 'litto-lib/components/common';
import { priceParser } from 'litto-lib/helpers';
import React from 'react';

import { ModalPriceDetails } from 'components/shared';
import { IOrderCheckout } from 'services/shop/order/interfaces';

interface ICartSummaryProps {
	className?: string;
	boxed?: boolean;
	skeleton?: boolean;
	order?: IOrderCheckout;
	refetchOrder?: () => void;
	priceDetailsModalState?: [
		boolean,
		React.Dispatch<React.SetStateAction<boolean>>
	];
	afterAdjustments?: React.ReactNode;
	afterTotal?: React.ReactNode;
	type?: 'accommodation' | 'experience';
	error?: any;
}

export const CartSummary = ({
	className = '',
	boxed,
	skeleton,
	priceDetailsModalState,
	order,
	afterAdjustments,
	afterTotal,
	type,
	error
}: ICartSummaryProps) => {
	const wrapperClass = `
		${className} ${boxed ? 'bg-gray-50  px-6 md:px-10 py-5' : ''}
		flex flex-col justify-center text-md rounded-lg`;

	const internalPriceDetailsModalState = React.useState<boolean>(false);

	const [showPriceDetailsModal, setShowPriceDetailsModal] =
		priceDetailsModalState || internalPriceDetailsModalState;

	if (order) {
		const { items, itemsOriginalTotal, total } = order;
		const adjustments = order.adjustments;

		if (type === 'experience') {
			return (
				<>
					<div className={wrapperClass}>
						<div className="flex flex-col gap-2">
							{type === 'experience' &&
								items?.map(item => (
									<div key={item['@id']} className="flex justify-between">
										<div className="space-x-2">
											<span>
												{item.quantity} {item.productName}
											</span>
											<span>x</span>
											<span>{priceParser(item.unitPrice)}</span>
										</div>
										<span>{priceParser(item.totalOriginalPrice)}</span>
									</div>
								))}
						</div>
						<div className="my-5 border-0 border-b border-solid border-gray-300 pb-1 ">
							{adjustments?.map(adjustment => (
								<div
									key={adjustment.label}
									className="mb-2 flex items-center justify-between "
								>
									<p>{adjustment.label}</p>
									<p className="font-extrabold">{`${priceParser(
										adjustment.amount
									)}`}</p>
								</div>
							))}
							{afterAdjustments}
						</div>

						<div className="flex items-center justify-between font-extrabold">
							<div>
								<p>Total</p>
								{afterTotal}
							</div>
							<p>{priceParser(total)}</p>
						</div>
						{type === 'experience' && (
							<span className="text-sm mt-2">
								{order?.cancellationNoticeExperience}
							</span>
						)}
					</div>
					<Modal
						headerText="Pricing"
						isOpen={showPriceDetailsModal}
						onRequestClose={() => setShowPriceDetailsModal(false)}
						customWidth="max-w-md"
					>
						<ModalPriceDetails
							items={order.items}
							itemsOriginalTotal={itemsOriginalTotal}
						/>
					</Modal>
				</>
			);
		}

		return (
			<>
				<div className={wrapperClass}>
					{/* <div>
						{type === 'experience' &&
							items?.map(item => (
								<div key={item['@id']}>
									<span>{item.productName}</span>
								</div>
							))}
					</div> */}
					<div className="mb-5 border-0 border-b border-solid border-gray-300 pb-1 ">
						<div className="mb-5 flex items-center justify-between ">
							<div className="flex gap-2">
								<p>
									{items.length === 1
										? `1 Night`
										: `${items.length + ' Nights'}`}
								</p>
								<p className="text-xs text-gray-400 translate-y-1">{'•'}</p>

								<button
									className="text-sm underline hover:text-primary-500 active:text-black"
									onClick={() => setShowPriceDetailsModal(true)}
								>
									View details
								</button>
							</div>
							<p className="font-extrabold">{`${priceParser(
								itemsOriginalTotal
							)}`}</p>
						</div>

						{adjustments?.map(adjustment => (
							<div
								key={adjustment.label}
								className="mb-2 flex items-center justify-between "
							>
								<p>{adjustment.label}</p>
								<p className="font-extrabold">{`${priceParser(
									adjustment.amount
								)}`}</p>
							</div>
						))}
						{afterAdjustments}
					</div>

					<div className="flex items-center justify-between font-extrabold">
						<div>
							<p>Total</p>
							{afterTotal}
						</div>
						<p>{priceParser(total)}</p>
					</div>
				</div>
				<Modal
					headerText="Pricing"
					isOpen={showPriceDetailsModal}
					onRequestClose={() => setShowPriceDetailsModal(false)}
					customWidth="max-w-md"
				>
					<ModalPriceDetails
						items={order.items}
						itemsOriginalTotal={itemsOriginalTotal}
					/>
				</Modal>
			</>
		);
	}

	if (skeleton) {
		return (
			<div className={wrapperClass + ' animate-pulse'}>
				<SkeletonText
					bgClass={boxed ? 'bg-gray-50' : 'bg-white'}
					className="mb-4 text-lg"
					rowSize={6}
				/>
				<div className="mb-5 flex items-center justify-between">
					<SkeletonText
						bgClass={boxed ? 'bg-gray-50' : 'bg-white'}
						rowSize={9}
					/>
					<div className="w-1/6">
						<SkeletonText
							bgClass={boxed ? 'bg-gray-50' : 'bg-white'}
							rowSize={12}
						/>
					</div>
				</div>
				<hr className="mb-5 border-gray-300" />
				<div className="mb-5 flex items-center justify-between">
					<div className="w-full">
						<SkeletonText
							bgClass={boxed ? 'bg-gray-50' : 'bg-white'}
							rowSize={4}
						/>
						<div className="w-1/6">
							<SkeletonText
								className="text-sm"
								bgClass={boxed ? 'bg-gray-50' : 'bg-white'}
								rowSize={12}
							/>
						</div>
					</div>
					<div className="w-1/6">
						<SkeletonText
							className="font-extrabold"
							bgClass={boxed ? 'bg-gray-50' : 'bg-white'}
							rowSize={12}
						/>
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div
				className={`${wrapperClass} rounded-lg border border-red bg-red/10 px-5 py-4 text-sm  text-red`}
			>
				<h3 className="text-center font-semibold">{error?.['hydra:title']}</h3>
				<p className="text-center text-md">{error?.['hydra:description']}</p>
			</div>
		);
	}

	return (
		<div className={wrapperClass}>
			<p className="text-center text-gray-600">
				{type === 'experience'
					? 'Select date and time to get prices'
					: 'Select dates to get prices'}
			</p>
		</div>
	);
};
